import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const neighborhoodFarmApi = createApi({
  reducerPath: "neighborhoodFarmApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["NeighborhoodFarm", "FarmStats"],
  endpoints: (builder) => ({
    // Get all farms
    getAllFarms: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append("status", params.status);
        if (params.search) queryParams.append("search", params.search);
        if (params.priority) queryParams.append("priority", params.priority);
        if (params.sortBy) queryParams.append("sortBy", params.sortBy);
        if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
        if (params.limit) queryParams.append("limit", params.limit);
        if (params.skip) queryParams.append("skip", params.skip);
        return `/neighborhood-farms?${queryParams.toString()}`;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: "NeighborhoodFarm", id: _id })),
              { type: "NeighborhoodFarm", id: "LIST" },
            ]
          : [{ type: "NeighborhoodFarm", id: "LIST" }],
    }),

    // Get single farm
    getFarmById: builder.query({
      query: (id) => `/neighborhood-farms/${id}`,
      providesTags: (result, error, id) => [{ type: "NeighborhoodFarm", id }],
    }),

    // Create farm
    createFarm: builder.mutation({
      query: (farmData) => ({
        url: "/neighborhood-farms",
        method: "POST",
        body: farmData,
      }),
      invalidatesTags: [
        { type: "NeighborhoodFarm", id: "LIST" },
        { type: "FarmStats" },
      ],
    }),

    // Update farm
    updateFarm: builder.mutation({
      query: ({ id, ...farmData }) => ({
        url: `/neighborhood-farms/${id}`,
        method: "PUT",
        body: farmData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "NeighborhoodFarm", id },
        { type: "NeighborhoodFarm", id: "LIST" },
        { type: "FarmStats" },
      ],
    }),

    // Delete farm
    deleteFarm: builder.mutation({
      query: (id) => ({
        url: `/neighborhood-farms/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "NeighborhoodFarm", id: "LIST" },
        { type: "FarmStats" },
      ],
    }),

    // Archive farm
    archiveFarm: builder.mutation({
      query: (id) => ({
        url: `/neighborhood-farms/${id}/archive`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "NeighborhoodFarm", id: "LIST" },
        { type: "FarmStats" },
      ],
    }),

    // Duplicate farm
    duplicateFarm: builder.mutation({
      query: ({ id, name }) => ({
        url: `/neighborhood-farms/${id}/duplicate`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: [
        { type: "NeighborhoodFarm", id: "LIST" },
        { type: "FarmStats" },
      ],
    }),

    // Add activity
    addActivity: builder.mutation({
      query: ({ farmId, ...activityData }) => ({
        url: `/neighborhood-farms/${farmId}/activities`,
        method: "POST",
        body: activityData,
      }),
      invalidatesTags: (result, error, { farmId }) => [
        { type: "NeighborhoodFarm", id: farmId },
        { type: "NeighborhoodFarm", id: "LIST" },
        { type: "FarmStats" },
      ],
    }),

    // Update activity
    updateActivity: builder.mutation({
      query: ({ farmId, activityId, ...activityData }) => ({
        url: `/neighborhood-farms/${farmId}/activities/${activityId}`,
        method: "PUT",
        body: activityData,
      }),
      invalidatesTags: (result, error, { farmId }) => [
        { type: "NeighborhoodFarm", id: farmId },
        { type: "FarmStats" },
      ],
    }),

    // Delete activity
    deleteActivity: builder.mutation({
      query: ({ farmId, activityId }) => ({
        url: `/neighborhood-farms/${farmId}/activities/${activityId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { farmId }) => [
        { type: "NeighborhoodFarm", id: farmId },
        { type: "FarmStats" },
      ],
    }),

    // Get stats
    getFarmStats: builder.query({
      query: () => "/neighborhood-farms/stats",
      providesTags: [{ type: "FarmStats" }],
    }),

    // Get upcoming activities
    getUpcomingActivities: builder.query({
      query: () => "/neighborhood-farms/upcoming",
    }),

    // Record listing
    recordListing: builder.mutation({
      query: ({ farmId, ...listingData }) => ({
        url: `/neighborhood-farms/${farmId}/listing`,
        method: "POST",
        body: listingData,
      }),
      invalidatesTags: (result, error, { farmId }) => [
        { type: "NeighborhoodFarm", id: farmId },
        { type: "FarmStats" },
      ],
    }),
  }),
});

export const {
  useGetAllFarmsQuery,
  useGetFarmByIdQuery,
  useCreateFarmMutation,
  useUpdateFarmMutation,
  useDeleteFarmMutation,
  useArchiveFarmMutation,
  useDuplicateFarmMutation,
  useAddActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  useGetFarmStatsQuery,
  useGetUpcomingActivitiesQuery,
  useRecordListingMutation,
} = neighborhoodFarmApi;
